import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { ClockIcon } from '@navikt/aksel-icons';
import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import {
  useRemindWorkEnvironmentTeamMutation,
  useWorkEnvironmentSurveyProgressQuery,
} from '../../hooks/queries/useWorkEnvironmentSurveyProgress';
import { AnsweredCard } from './components/AnsweredCard';
import { reminderStatus } from './reminderView';
import { NextStepCard } from './components/NextStepCard';
import { ProcessHero } from './components/ProcessHero';
import { ShareCard } from './components/ShareCard';
import { currentStepIndex, dayAndMonth, surveyLink } from './surveyProcessView';
import classes from './WorkEnvironmentSurveyProcess.module.css';

/** The survey while the team answers it */
export const WorkEnvironmentSurveyProcess = ({ survey }: { survey: WorkEnvironmentSurvey }): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data: progress, isLoading, isError } = useWorkEnvironmentSurveyProgressQuery(survey.id);
  const remind = useRemindWorkEnvironmentTeamMutation(survey.id);
  const teamName = survey.teamName;
  const deadline = progress?.responseDeadline ?? null;

  return (
    <div data-size="md">
      <ProcessHero
        title={survey.title}
        intro={
          deadline
            ? t('workEnvironmentProcess.intro', { team: teamName, date: dayAndMonth(deadline, i18n.language) })
            : t('workEnvironmentProcess.introWithoutDeadline', { team: teamName })
        }
        tag={{ label: t('workEnvironmentProcess.waiting'), color: 'info', icon: <ClockIcon aria-hidden /> }}
        process={{ current: currentStepIndex(survey.status), createdAt: survey.createdAt, responseDeadline: deadline }}
      />
      <div className={classes.layout}>
        {isLoading && (
          <div className={classes.main} aria-busy="true">
            <Skeleton variant="rectangle" width="100%" height={320} />
          </div>
        )}
        {isError && (
          <Alert data-color="danger" className={classes.main}>
            <Paragraph>{t('workEnvironmentProcess.error')}</Paragraph>
          </Alert>
        )}
        {progress && (
          <AnsweredCard
            progress={progress}
            remind={{
              run: () => remind.mutate(),
              pending: remind.isPending,
              ...reminderStatus(remind, t, i18n.language),
            }}
          />
        )}
        <div className={classes.side}>
          {/* When the risk is ready comes first, so nobody waits for it without knowing */}
          <NextStepCard responseDeadline={deadline} responses={progress?.responses ?? null} />
          <ShareCard
            link={surveyLink(window.location.origin, survey.uuid)}
            sharing={{ surveyId: survey.id, teamId: survey.teamId }}
          />
        </div>
      </div>
    </div>
  );
};
